import { z } from "zod";
import { AdminReason } from "./admin.ts";
import { DisplayName, UserId } from "./user.ts";

/**
 * Приглашение преподавателя (раздел 9 docs/admin-panel-design.md).
 *
 * Выпускает админ в панели, гасит приглашённый первым входом через Google: код
 * едет в state-cookie входа, отдельной ручки погашения нет (9.5). Здесь формы
 * ручек `/admin/v1/invites*` и правило состояния, общее для сервера и панели.
 *
 * ⚠️ Кода в этих типах нет нигде, кроме ответа выпуска (`AdminInviteIssueResponse`),
 * и там он внутри готовой ссылки: код отдаётся ОДИН раз и больше не доступен
 * ничем, в базе лежит только его SHA-256 (9.2).
 *
 * ⚠️ `sentToEmail` справочный: он для строки «кому отправлено» и ни для одной
 * проверки. Погашение адрес не сравнивает (9.3), авторизует только код. С
 * 27.09.2026 по нему ещё и уходит письмо, если его попросили
 * (docs/email-design.md): адрес стал конвертом, но ключом так и не стал.
 */

/** Срок жизни по умолчанию, сутки (9.4). */
export const TEACHER_INVITE_DEFAULT_DAYS = 14;
/**
 * Верхняя граница срока. Приглашение это разовое разрешение завести аккаунт, и
 * висящая месяцами непогашенная авторизация это лишний ключ (9.4).
 */
export const TEACHER_INVITE_MAX_DAYS = 30;
/** Заметка для админа: «Рита из чата репетиторов». Та же граница, что `CHECK` в базе. */
export const TEACHER_INVITE_NOTE_MAX = 200;
/** Страница выдачи списка приглашений. */
export const ADMIN_INVITE_PAGE_SIZE = 100;

/** Адрес страницы приглашения в кабинете. Код стоит во фрагменте: `/teacher-invite#<код>`. */
export const TEACHER_INVITE_PATH = "/teacher-invite";

/**
 * Адрес приглашения. Отдельной константой, потому что им же проверяется почта
 * в заявке «позвать коллегу» (`normalizeReferralContact`): панель подставляет
 * контакт заявки в форму выпуска, и два валидатора однажды разъехались бы
 * ровно на адресе, который прошёл заявку и не проходит выпуск.
 */
export const TeacherInviteEmail = z.string().trim().email().max(320);

/**
 * Выпуск. `sendEmail` просит отправить ссылку письмом на `sentToEmail` в ту же
 * секунду (docs/email-design.md, раздел 6), поэтому без адреса он не имеет
 * смысла и отклоняется формой, а не ручкой. Поле необязательное: панель при
 * выключенной почте его не шлёт вовсе, и тело выпуска у неё прежнее.
 */
export const AdminInviteIssueRequest = z
  .object({
    sentToEmail: TeacherInviteEmail.optional(),
    note: z.string().trim().min(1).max(TEACHER_INVITE_NOTE_MAX).optional(),
    expiresInDays: z.number().int().min(1).max(TEACHER_INVITE_MAX_DAYS).optional(),
    sendEmail: z.boolean().optional(),
    /**
     * Выпуск по заявке «позвать коллегу» (4.3 docs/colleague-referral-design.md):
     * заявка закрывается в той же транзакции, что выпуск. Без поля тело
     * выпуска прежнее буква в букву.
     */
    referralId: z.string().uuid().optional(),
  })
  .refine((b) => b.sendEmail !== true || b.sentToEmail !== undefined, {
    message: "для письма нужен адрес",
    path: ["sentToEmail"],
  });
export type AdminInviteIssueRequest = z.infer<typeof AdminInviteIssueRequest>;

/** Отзыв: причина обязательна, как у каждого пишущего действия панели (4.3). */
export const AdminInviteRevokeRequest = z.object({ reason: AdminReason });
export type AdminInviteRevokeRequest = z.infer<typeof AdminInviteRevokeRequest>;

export const TeacherInviteState = z.enum(["pending", "redeemed", "revoked", "expired"]);
export type TeacherInviteState = z.infer<typeof TeacherInviteState>;

/**
 * Состояние приглашения из его дат.
 *
 * Порядок проверок и есть правило: погашенное остаётся погашенным и после
 * срока, отозванное остаётся отозванным и после срока. Отозвать погашенное
 * нельзя (ручка отказывает), поэтому пара «погашено и отозвано» не встречается,
 * а если однажды встретится, честнее показать «погашено»: аккаунт заведён.
 *
 * Граница срока строгая: в момент `expiresAt` приглашение уже истекло, как и в
 * предикате погашения на сервере.
 */
export function teacherInviteState(
  invite: { expiresAt: number; redeemedAt: number | null; revokedAt: number | null },
  now: number,
): TeacherInviteState {
  if (invite.redeemedAt !== null) return "redeemed";
  if (invite.revokedAt !== null) return "revoked";
  if (invite.expiresAt <= now) return "expired";
  return "pending";
}

/**
 * Исход письма с приглашением (docs/email-design.md, раздел 6). Четвёртый исход,
 * «письмо не просили», это `null`, а не значение: так же он лежит и в базе.
 *
 * - `sent`: провайдер принял адрес. Это ещё не доставка: возвраты приходят
 *   позже, а вебхуков у нас нет;
 * - `failed`: провайдер отказал, причина в `emailError`;
 * - `unknown`: ответа не дождались или не поняли его, письмо могло и уйти.
 */
export const InviteEmailStatus = z.enum(["sent", "failed", "unknown"]);
export type InviteEmailStatus = z.infer<typeof InviteEmailStatus>;

export const AdminInvitePerson = z.object({
  userId: UserId,
  displayName: DisplayName,
});
export type AdminInvitePerson = z.infer<typeof AdminInvitePerson>;

export const AdminInviteView = z.object({
  id: z.string().uuid(),
  createdAt: z.number().int(),
  expiresAt: z.number().int(),
  sentToEmail: z.string().nullable(),
  note: z.string().nullable(),
  issuedBy: AdminInvitePerson,
  redeemedAt: z.number().int().nullable(),
  /** `null` и при непогашенном, и если заведённого человека однажды удалят физически. */
  redeemedUser: AdminInvitePerson.nullable(),
  revokedAt: z.number().int().nullable(),
  /** Посчитано сервером на момент ответа тем же `teacherInviteState`. */
  state: TeacherInviteState,
  /** `null`: письмо не просили (или приглашение выпущено до появления почты). */
  emailStatus: InviteEmailStatus.nullable(),
  /**
   * Причина отказа провайдера у `failed`: тип ошибки SES (`MessageRejected`,
   * `TooManyRequestsException`) или `http_<код>`. Текста провайдера здесь нет
   * никогда, он может нести адрес.
   */
  emailError: z.string().nullable(),
  emailAt: z.number().int().nullable(),
});
export type AdminInviteView = z.infer<typeof AdminInviteView>;

export const AdminInviteListResponse = z.object({
  invites: z.array(AdminInviteView),
  nextCursor: z.string().uuid().nullable(),
});
export type AdminInviteListResponse = z.infer<typeof AdminInviteListResponse>;

/**
 * Ответ выпуска. Ссылка несёт код, и это единственный раз, когда он виден:
 * потерянную ссылку не восстановить, только отозвать и выпустить новую.
 */
export const AdminInviteIssueResponse = z.object({
  invite: AdminInviteView,
  url: z.string().url(),
});
export type AdminInviteIssueResponse = z.infer<typeof AdminInviteIssueResponse>;
